import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Calendar, Users } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    guest_count: "",
    event_type: "",
    budget_range: "",
    dietary_requirements: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Quote request submitted successfully!", {
        description: "We'll get back to you within 24 hours."
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        guest_count: "",
        event_type: "",
        budget_range: "",
        dietary_requirements: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to submit request", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFBF7] grain-overlay"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-sm tracking-widest uppercase font-semibold text-orange-700">
                Let's Plan Your Menu
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-800">
                Request a Quote
              </h2>
              <p className="text-lg md:text-xl leading-relaxed text-gray-600 pt-4">
                Tell us about your event, and we'll take care of the rest. From menu planning to execution, Kamaliicious makes catering feel effortless.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-6 pt-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-orange-700" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Call us</p>
                  <p className="font-semibold text-gray-800">301-412-5210</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-orange-700" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email us</p>
                  <p className="font-semibold text-gray-800">kamaliicious15@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-orange-700" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="font-semibold text-gray-800">Serving the Greater Metro Area</p>
                </div>
              </div>
            </div>

            {/* Quote */}
            <div className="pt-8 border-l-4 border-orange-700 pl-6">
              <p className="font-accent text-xl italic text-gray-700">
                "Reliable. Thoughtful. Delicious."
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-stone-100">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
              {/* Contact Details Group */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-sm flex items-center justify-center">1</span>
                  Contact Details
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                      className="bg-stone-50 border-stone-200 focus:border-orange-500"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      className="bg-stone-50 border-stone-200 focus:border-orange-500"
                      data-testid="input-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    required
                    className="bg-stone-50 border-stone-200 focus:border-orange-500"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Event Details Group */}
              <div className="space-y-4 pt-4 border-t border-stone-100">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-sm flex items-center justify-center">2</span>
                  Event Details
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_date" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      Event Date *
                    </Label>
                    <Input
                      id="event_date"
                      name="event_date"
                      type="date"
                      value={formData.event_date}
                      onChange={handleInputChange}
                      required
                      className="bg-stone-50 border-stone-200 focus:border-orange-500"
                      data-testid="input-event-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guest_count" className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      Guest Count *
                    </Label>
                    <Select
                      value={formData.guest_count}
                      onValueChange={(value) => handleSelectChange("guest_count", value)}
                    >
                      <SelectTrigger className="bg-stone-50 border-stone-200" data-testid="select-guest-count">
                        <SelectValue placeholder="Select guest count" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10-25">10-25 guests</SelectItem>
                        <SelectItem value="25-50">25-50 guests</SelectItem>
                        <SelectItem value="50-100">50-100 guests</SelectItem>
                        <SelectItem value="100-200">100-200 guests</SelectItem>
                        <SelectItem value="200-500">200-500 guests</SelectItem>
                        <SelectItem value="500+">500+ guests</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_type">Event Type *</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => handleSelectChange("event_type", value)}
                    >
                      <SelectTrigger className="bg-stone-50 border-stone-200" data-testid="select-event-type">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corporate-lunch">Corporate Lunch</SelectItem>
                        <SelectItem value="corporate-event">Corporate Event</SelectItem>
                        <SelectItem value="wedding">Wedding</SelectItem>
                        <SelectItem value="birthday">Birthday Party</SelectItem>
                        <SelectItem value="anniversary">Anniversary</SelectItem>
                        <SelectItem value="festival">Festival/Puja</SelectItem>
                        <SelectItem value="cocktail">Cocktail Party</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget_range">Budget Range</Label>
                    <Select
                      value={formData.budget_range}
                      onValueChange={(value) => handleSelectChange("budget_range", value)}
                    >
                      <SelectTrigger className="bg-stone-50 border-stone-200" data-testid="select-budget">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-500">Under $500</SelectItem>
                        <SelectItem value="500-1000">$500 - $1,000</SelectItem>
                        <SelectItem value="1000-2500">$1,000 - $2,500</SelectItem>
                        <SelectItem value="2500-5000">$2,500 - $5,000</SelectItem>
                        <SelectItem value="5000-10000">$5,000 - $10,000</SelectItem>
                        <SelectItem value="10000+">$10,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Additional Info Group */}
              <div className="space-y-4 pt-4 border-t border-stone-100">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-sm flex items-center justify-center">3</span>
                  Additional Information
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="dietary_requirements">Dietary Requirements</Label>
                  <Input
                    id="dietary_requirements"
                    name="dietary_requirements"
                    value={formData.dietary_requirements}
                    onChange={handleInputChange}
                    placeholder="Vegetarian, vegan, gluten-free, allergies..."
                    className="bg-stone-50 border-stone-200 focus:border-orange-500"
                    data-testid="input-dietary"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Tell us about your event</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Share any specific requests, themes, or details about your event..."
                    rows={4}
                    className="bg-stone-50 border-stone-200 focus:border-orange-500 resize-none"
                    data-testid="input-message"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-700 hover:bg-orange-800 text-white rounded-full py-6 text-lg font-medium shadow-lg shadow-orange-700/20 transition-transform hover:scale-[1.02]"
                data-testid="submit-quote-btn"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Get a Quote
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
